// Captain speech bubble (spec-captain §Runtime) — TypeScript port of
// chess-td's KingSpeech engine: 12 ms/char typewriter (3 ms for spaces and
// punctuation), hold max(2500, 1400 + len*40) ms, spring CSS entrance.
// Gating lives one level up in CaptainMoods (BARK_COOLDOWN_MS between
// barks, BARK_PREEMPT moods always speak); show() re-applies the same
// policy as defense-in-depth plus a 50% coin-flip on bored/idle, and say()
// bypasses everything behind a mood lock. Vanilla DOM, no framework; the
// typewriter chirp is handed to an onChirp callback (the parent wires it
// to the audio engine's square-wave blip).

import { FONT_UI, colors, ensureUiStyles, pixelShadow } from '../ui/theme.js';
import type { CaptainMood } from './dictionary.js';
import { BARK_COOLDOWN_MS, BARK_PREEMPT } from './moods.js';

const CHAR_MS = 12; // ms per visible character
const PUNCT_MS = 3; // ms for spaces / punctuation (nearly instant)
const HOLD_MS_MIN = 2500;
const FADE_MS = 260;

/** Mood → frame/tail tint: the comms chip telegraphs severity before the
 * first word lands. Red = hull in danger, pink = boss incoming, amber =
 * wary, gold = triumph, null = neutral panel frame (ambient chatter). */
const MOOD_TINT: Record<CaptainMood, string | null> = {
  hit: colors.hp,
  death: colors.hp,
  boss: '#ff3d81',
  wary: '#ff9540',
  flawless: colors.accent,
  streak: colors.accent,
  wavestart: null,
  bored: null,
  idle: null,
};

// Long lines need their full read: a flat 2.5 s dwell would vanish a
// 160-char line mid-type (12 ms/char ≈ 2 s of typing alone).
function computeHoldMs(text: string): number {
  return Math.max(HOLD_MS_MIN, 1400 + text.length * 40);
}

export class CaptainSpeech {
  private readonly el: HTMLDivElement;
  private readonly portraitFrame: HTMLDivElement;
  private readonly tail: HTMLDivElement;
  private readonly textNode: Text;
  private readonly onChirp: (pitch: number) => void;

  private _typeTimer: number | null = null;
  private _hideTimer: number | null = null;
  private _displayTimer: number | null = null;
  private _typeIndex = 0;
  private _typeFullText = '';
  private _lastShownAt = 0;
  private _moodLockUntil = 0;
  private _disposed = false;

  /** The chip element, so the HUD can host it inside the corner module. */
  get element(): HTMLElement {
    return this.el;
  }

  /**
   * Comms chip, hosted INSIDE the HUD's pilot-seat row (hud.ts
   * hostCaptainSpeech): it takes the skill boxes' place right of the
   * portrait while the captain speaks — one row, face + words. The in-chip
   * portrait is hidden (the seat's portrait is right there); the tail
   * points left at it. `onChirp` fires per typed letter (throttled to every
   * 3rd) with a pitch in ~[0.9, 1.2]; default no-op until the parent wires
   * the audio engine.
   */
  constructor(container: HTMLElement, onChirp: (pitch: number) => void = () => {}) {
    this.onChirp = onChirp;
    // The chip's position comes from the shared stylesheet — make sure it
    // is injected even if the HUD hasn't been built yet.
    ensureUiStyles();

    const el = document.createElement('div');
    el.className = 'la-captain-speech';
    el.setAttribute('aria-live', 'polite');
    // Hug the text (no grow): a one-liner gets a compact chip instead of a
    // full-width gold slab. Standard panel frame; the gold tail against the
    // portrait carries the accent. Max-width caps the measure so long lines
    // wrap to 2–3 readable rows instead of spanning the screen as a banner.
    el.style.cssText = `
      position: relative;
      flex: 0 1 auto;
      min-width: 0;
      max-width: min(340px, 100%);
      align-self: center;
      margin: 0;
      padding: 6px 10px 6px 8px;
      box-sizing: border-box;
      background: ${colors.panelBg};
      box-shadow: ${pixelShadow(colors.panelBorder, 2)};
      color: ${colors.text};
      font-family: ${FONT_UI};
      font-size: 12px;
      letter-spacing: 0.04em;
      line-height: 1.3;
      text-align: left;
      pointer-events: none;
      opacity: 0;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: anywhere;
      will-change: transform, opacity;
      transition: opacity ${FADE_MS}ms ease, transform 320ms cubic-bezier(.2, 1.4, .3, 1);
      display: none;
      align-items: center;
      gap: 10px;
    `;
    // Speech-bubble tail pointing LEFT at the seat's portrait beside it.
    const tail = document.createElement('div');
    tail.style.cssText = `
      position: absolute; left: -10px; top: 50%; margin-top: -7px;
      width: 0; height: 0;
      border-top: 7px solid transparent;
      border-bottom: 7px solid transparent;
      border-right: 10px solid ${colors.accent};
    `;
    el.appendChild(tail);
    this.tail = tail;

    const portraitFrame = document.createElement('div');
    portraitFrame.className = 'la-captain-speech-portrait';
    portraitFrame.style.cssText = `
      display: none;
      flex: 0 0 48px;
      width: 48px;
      height: 48px;
      background: ${colors.barTrack};
      box-shadow: inset 0 0 0 2px ${colors.panelBorder};
    `;
    el.appendChild(portraitFrame);
    this.portraitFrame = portraitFrame;

    const textWrap = document.createElement('div');
    textWrap.style.cssText = 'flex: 1 1 auto; min-width: 0;';
    this.textNode = document.createTextNode('');
    textWrap.appendChild(this.textNode);
    el.appendChild(textWrap);
    container.appendChild(el);
    this.el = el;
  }

  /**
   * Mood-driven line (text already picked via pickLine by the caller).
   * Gated: say() lock, BARK_COOLDOWN_MS for non-preempting moods, 50% skip
   * on bored/idle. BARK_PREEMPT moods (death, hit, flawless, boss) always
   * speak. CaptainMoods applies the same gate upstream; this is the
   * defense-in-depth copy for direct show() callers.
   */
  show(line: string, mood: CaptainMood, portrait: HTMLCanvasElement): void {
    if (this._disposed || !line) return;
    const now = performance.now();
    if (now < this._moodLockUntil) return;
    if (!BARK_PREEMPT[mood]) {
      if (now - this._lastShownAt < BARK_COOLDOWN_MS) return;
      // Even outside the cooldown, ambient moods only pop occasionally so
      // they don't fire on every quiet gap.
      if ((mood === 'bored' || mood === 'idle') && Math.random() < 0.5) return;
    }
    this._lastShownAt = now;
    this._show(line, portrait, mood);
  }

  /** Bypass the gating and show `text` directly; mood lines are locked out for `lockMs`. */
  say(text: string, lockMs = 4500, portrait: HTMLCanvasElement | null = null): void {
    if (this._disposed || !text) return;
    const now = performance.now();
    this._moodLockUntil = now + lockMs;
    this._lastShownAt = now;
    this._show(text, portrait);
  }

  /** Fade out and collapse the row (the panel shrinks back when silent). */
  hide(): void {
    this.el.style.opacity = '0';
    this.el.style.transform = 'translate(0, 6px) scale(0.94)';
    if (this._displayTimer !== null) window.clearTimeout(this._displayTimer);
    this._displayTimer = window.setTimeout(() => {
      this.el.style.display = 'none';
      this._displayTimer = null;
    }, FADE_MS + 60);
  }

  /** Stop timers and remove the chip from the DOM. */
  dispose(): void {
    this._disposed = true;
    if (this._typeTimer !== null) { window.clearTimeout(this._typeTimer); this._typeTimer = null; }
    if (this._hideTimer !== null) { window.clearTimeout(this._hideTimer); this._hideTimer = null; }
    if (this._displayTimer !== null) { window.clearTimeout(this._displayTimer); this._displayTimer = null; }
    this.el.remove();
  }

  private _show(text: string, portrait: HTMLCanvasElement | null, mood?: CaptainMood): void {
    // If a previous line is still typing, snap it to completion first so
    // the next line starts cleanly.
    if (this._typeTimer !== null) this._finishType();
    if (portrait) this._setPortrait(portrait);

    // Severity cue: tinted frame + tail for alarm/triumph moods; the ambient
    // default stays the quiet panel frame with the gold tail.
    const tint = mood !== undefined ? MOOD_TINT[mood] : null;
    this.el.style.boxShadow = pixelShadow(tint ?? colors.panelBorder, 2);
    this.tail.style.borderRightColor = tint ?? colors.accent;

    this._typeFullText = text;
    this._typeIndex = 0;
    this.textNode.nodeValue = '';

    // Kick the spring animation by forcing a style flush. The pop grows
    // inside the corner module (no fixed positioning — the panel owns it).
    if (this._displayTimer !== null) { window.clearTimeout(this._displayTimer); this._displayTimer = null; }
    this.el.style.display = 'flex';
    this.el.style.opacity = '0';
    this.el.style.transform = 'translate(0, 10px) scale(0.82)';
    void this.el.offsetWidth;
    this.el.style.opacity = '1';
    this.el.style.transform = 'translate(0, 0) scale(1)';

    if (this._hideTimer !== null) { window.clearTimeout(this._hideTimer); this._hideTimer = null; }
    // Start typing immediately; the spring entrance plays underneath.
    this._typeNextChar();
  }

  private _setPortrait(portrait: HTMLCanvasElement): void {
    portrait.style.width = '48px';
    portrait.style.height = '48px';
    portrait.style.imageRendering = 'pixelated';
    portrait.style.display = 'block';
    this.portraitFrame.replaceChildren(portrait);
  }

  private _typeNextChar(): void {
    if (this._disposed) return;
    if (this._typeIndex >= this._typeFullText.length) {
      // Full text revealed — start the hold timer before fading out.
      this._typeTimer = null;
      this._hideTimer = window.setTimeout(() => this.hide(), computeHoldMs(this._typeFullText));
      return;
    }
    const ch = this._typeFullText.charAt(this._typeIndex);
    this._typeIndex++;
    this.textNode.nodeValue = this._typeFullText.slice(0, this._typeIndex);
    const isLetter = /[A-Za-z0-9]/.test(ch);
    // Chirp throttled to every 3rd letter so long lines don't dump an
    // oscillator per character at the audio engine.
    if (isLetter && this._typeIndex % 3 === 0) {
      this.onChirp(0.9 + Math.random() * 0.3);
    }
    this._typeTimer = window.setTimeout(() => this._typeNextChar(), isLetter ? CHAR_MS : PUNCT_MS);
  }

  private _finishType(): void {
    if (this._typeTimer !== null) { window.clearTimeout(this._typeTimer); this._typeTimer = null; }
    this.textNode.nodeValue = this._typeFullText;
    this._typeIndex = this._typeFullText.length;
    if (this._hideTimer !== null) window.clearTimeout(this._hideTimer);
    this._hideTimer = window.setTimeout(() => this.hide(), computeHoldMs(this._typeFullText));
  }
}
